import { useMemo } from 'react';
import Input from 'components/molecules/Input';
import SelectKeywords from 'components/molecules/SelectKeywords';
import Typography from 'components/atoms/Typography';
import ToggleButton from 'components/molecules/ToggleButton';
import Select from 'components/molecules/Select';
import TimeSelect from 'components/molecules/TimeSelect';
import Button from 'components/atoms/Button';
import Separator from 'components/atoms/Separator';
import Icon from 'components/atoms/Icon';

import { SelectOption } from 'utils/frontendTypes';
import { DevotionalTemplateConfig, DevotionalType, Templates } from 'utils/gloo-types';
import { Configs } from 'utils/constants';
import { generateNumberOptions } from 'utils/selectOptions';
import InputI18nWithState from 'components/molecules/InputI18nWithState';

interface SeriesTemplateConfigProps {
  template?: Partial<Templates>;
  handleChange: (data) => void;
  handleForm: (data) => void;
}

function SeriesTemplateConfig({ template, handleChange, handleForm }: SeriesTemplateConfigProps) {
  const templateConfig = template.config as DevotionalTemplateConfig;

  const updateConfigProperty = (key: keyof DevotionalTemplateConfig, value) => {
    handleChange((prevState) => {
      const config = prevState.config;
      config[key] = value;

      return {
        ...prevState,
        config,
      };
    });

    handleForm((prevState) => {
      const config = prevState.config;
      config[key] = value;

      return {
        ...prevState,
        config,
      };
    });
  };

  const updateConfigContentProperty = (key: keyof DevotionalTemplateConfig['content'], value) => {
    handleChange((prevState) => {
      const config = prevState.config;
      config.content[key] = value;

      return {
        ...prevState,
        config,
      };
    });

    handleForm((prevState) => {
      const config = prevState.config;
      config.content[key] = value;

      return {
        ...prevState,
        config,
      };
    });
  };

  const updateConfigContentMessage = (index: number, messagePropKey, value) => {
    const messages = templateConfig.content.messages;
    messages[index][messagePropKey] = value;
    updateConfigContentProperty('messages', messages);
  };

  const handleAddMessage = () => {
    const messages = templateConfig.content.messages;
    const latestMessage = messages[messages.length - 1];
    const newMessage = {
      dayNum: latestMessage?.dayNum ? latestMessage.dayNum + 1 : 1,
      time: latestMessage?.time || '12:00',
      message: '',
    };

    const newMessages = [...messages, newMessage];

    handleChange((prevState) => {
      const config = prevState.config;
      config.content.messages = newMessages;

      return {
        ...prevState,
        config,
      };
    });

    handleForm((prevState) => {
      const config = prevState.config;
      config.content.messages = newMessages;

      return {
        ...prevState,
        config,
      };
    });
  };

  const handleDeleteMessage = (index: number) => {
    const config = template.config as DevotionalTemplateConfig;
    config.content.messages.splice(index, 1);

    handleChange((prevState) => {
      return {
        ...prevState,
        config,
      };
    });

    handleForm((prevState) => {
      return {
        ...prevState,
        config,
      };
    });
  };

  const dayOptions = useMemo(
    () => generateNumberOptions({ end: Configs.Devotionals.MaxDaysOut, labelPrefix: 'Day ' }),
    [],
  );

  const createMessageButton = (
    <Button
      dataDurableId="createMessageButton"
      onClick={handleAddMessage}
      type="primary"
      className=""
      text="Create Message"
    />
  );

  return (
    <>
      <Input
        label="Name"
        required
        className="mb-2"
        value={templateConfig?.name}
        onChangeText={(t) => {
          updateConfigProperty('name', t);
        }}
      />
      <SelectKeywords
        dataDurableId="seriesTemplateSelectId"
        required
        className="border"
        label="Keywords"
        startingKeywords={templateConfig?.keywords}
        onChange={(t) => {
          updateConfigProperty('keywords', t);
        }}
        onCreate={(t) => {
          updateConfigProperty('keywords', [...templateConfig.keywords, t]);
        }}
      />
      <Input
        label="Description"
        isMultiLine
        rows={4}
        className="mb-2"
        value={templateConfig?.description}
        onChangeText={(t) => {
          updateConfigProperty('description', t);
        }}
        required
      />

      <Separator color="blackLight" className="my-4" />

      <div className="mb-2">
        <Typography className="pb-2 pl-1" variant="label">
          Series Type
        </Typography>

        <ToggleButton
          callback={(selected: DevotionalType) => {
            updateConfigProperty('devotionalType', selected);
          }}
          initialState={templateConfig?.devotionalType}
          left={{ label: 'Relative', value: DevotionalType.RELATIVE_DATE, icon: 'repeat' }}
          right={{ label: 'Fixed', value: DevotionalType.FIXED_DATE, icon: 'clock' }}
        />

        <InputI18nWithState
          containerStyles="mt-4"
          label="Message Sent When Subscribed"
          isMultiLine
          rows={2}
          initialState={templateConfig?.content?.subscribeMessage}
          callback={(t) => {
            updateConfigContentProperty('subscribeMessage', t);
          }}
        />
        <Typography className="pb-4 pl-1" variant="label">
          Series Messages
        </Typography>
        <section>
          <div className="flex content-end">{createMessageButton}</div>
          {templateConfig?.content?.messages &&
            templateConfig.content.messages.map((seriesMessage, index) => {
              return (
                <div
                  key={`message_${index}`}
                  className="my-4 w-full flex justify-between mb-4 relative focus-within:z-10 md:flex-row flex-col"
                >
                  <div className="flex-2 mr-4">
                    <div className="relative">
                      <div className="absolute top-0 left-0 z-50 flex items-center justify-center rounded-full text-white font-bold bg-blue text-xs w-5 h-5 m-2">
                        {index + 1}
                      </div>
                      <InputI18nWithState
                        initialState={seriesMessage?.message}
                        className="pl-10 pt-2 border border-blueLightest rounded shadow"
                        callback={(text) => {
                          updateConfigContentMessage(index, 'message', text);
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex-1 mr-2 my-1">
                    <Select
                      dataDurableId="seriesTemplateConfigSelectId"
                      className="border border-blueLightest rounded shadow mr-2"
                      options={dayOptions}
                      value={{ value: seriesMessage?.dayNum, label: `Day ${seriesMessage?.dayNum}` }}
                      onChange={(selectedOption: SelectOption) => {
                        updateConfigContentMessage(index, 'dayNum', selectedOption.value);
                      }}
                    />
                    <TimeSelect
                      className="border border-blueLightest rounded shadow mr-2 mt-1"
                      dataDurableId="seriesTemplateConfigTimeSelect"
                      generateOptions={{ intervalMin: Configs.Devotionals.TimeInterval }}
                      value={seriesMessage?.time}
                      onChange={(selectedOption: SelectOption) => {
                        updateConfigContentMessage(index, 'time', selectedOption.value);
                      }}
                    />
                  </div>
                  <Icon
                    className="flex items-center justify-center rounded-full text-white bg-red w-8 h-8 cursor-pointer"
                    name="trash-2"
                    onClick={() => handleDeleteMessage(index)}
                    size={18}
                  />
                </div>
              );
            })}
          {templateConfig?.content?.messages?.length > 0 && (
            <div className="flex content-end">{createMessageButton}</div>
          )}
        </section>
      </div>
    </>
  );
}

export default SeriesTemplateConfig;
